import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InputGroup } from '@blueprintjs/core';
import { toast } from 'sonner';
import { nameColumns, type SheetRow } from './api';
import { leadImportKeys } from './keys';
import { colLetter } from './ui';

/** Columns that need a name: every blank header with data, or all of them when row 1 is empty. */
function columnsToName(s: SheetRow): number[] {
  const issues = s.header_issues ?? [];
  if (issues.some((i) => i.code === 'empty_row1')) return Array.from({ length: s.column_count }, (_, i) => i);
  return issues.flatMap((i) => (i.code === 'blank_label' && i.position != null ? [i.position] : []));
}

/**
 * Name blank row-1 headers here instead of fixing the file. Mapping works by
 * column position, so a name only labels the column; no lead data changes.
 */
export default function NameHeaders({ batch, sheet, onNamed }: { batch: number; sheet: SheetRow; onNamed: () => void }) {
  const queryClient = useQueryClient();
  const positions = columnsToName(sheet);
  const [names, setNames] = useState<Record<number, string>>({});
  const sample = (pos: number) => (sheet.sample_rows ?? []).map((r) => r.cells[pos]?.trim()).find(Boolean);
  const save = useMutation({
    mutationFn: () => nameColumns(batch, sheet.id, Object.fromEntries(positions.map((p) => [p, names[p] ?? '']))),
    onSuccess: () => {
      toast.success(`Saved the headers of ${sheet.sheet_name}.`);
      queryClient.invalidateQueries({ queryKey: leadImportKeys.batch(batch) });
      onNamed();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const missing = positions.some((p) => !names[p]?.trim());

  return (
    <form className="li-nameheaders" aria-label={`Name the columns of ${sheet.sheet_name}`}
      onSubmit={(e) => { e.preventDefault(); if (!missing) save.mutate(); }}>
      <p className="li-nameheaders__lead">Name {positions.length === 1 ? 'it' : 'them'} here, or replace the file below.</p>
      {positions.map((p) => {
        const hint = sample(p);
        return (
          <label key={p} className="li-nameheaders__row">
            <span className="li-nameheaders__letter">Column {colLetter(p)}</span>
            <InputGroup value={names[p] ?? ''} maxLength={100} placeholder="Header name" disabled={save.isPending}
              aria-label={`Header for column ${colLetter(p)}`}
              onChange={(e) => setNames((cur) => ({ ...cur, [p]: e.target.value }))} />
            {hint && <span className="li-nameheaders__hint">e.g. {hint}</span>}
          </label>
        );
      })}
      <button type="submit" className="li-btn li-btn--primary" disabled={missing || save.isPending}>
        {save.isPending ? 'Saving…' : 'Save headers'}
      </button>
    </form>
  );
}
